import React from 'react';
import type { RadarGrupoRiscoContract } from '@sstexames/shared-types';

interface Props {
  dados?: RadarGrupoRiscoContract;
  tamanho?: number;
}

const GRUPOS = [
  { key: 'fisico', label: 'Físico (NR-09/15)', cor: '#10B981', angulo: 0 },
  { key: 'quimico', label: 'Químico (NR-09/15)', cor: '#EF4444', angulo: 60 },
  { key: 'biologico', label: 'Biológico (NR-32)', cor: '#B45309', angulo: 120 },
  { key: 'ergonomico', label: 'Ergonômico (NR-17)', cor: '#F59E0B', angulo: 180 },
  { key: 'acidente', label: 'Acidente (NR-12/35)', cor: '#3B82F6', angulo: 240 },
  { key: 'psicossocial', label: 'Psicossocial (NR-01)', cor: '#9333EA', angulo: 300 },
] as const;

export default function RadarRiscosChart({ dados, tamanho = 300 }: Props) {
  const radarDados: RadarGrupoRiscoContract = dados || {
    fisico: 45,
    quimico: 25,
    biologico: 15,
    ergonomico: 75,
    acidente: 40,
    psicossocial: 35,
  };

  const center = tamanho / 2;
  const raioMax = tamanho / 2 - 42;

  const getCoordenadas = (anguloGraus: number, valor: number) => {
    const raio = (Math.min(Math.max(valor, 0), 100) / 100) * raioMax;
    const rad = ((anguloGraus - 90) * Math.PI) / 180;
    const x = center + raio * Math.cos(rad);
    const y = center + raio * Math.sin(rad);
    return { x, y };
  };

  const pontosPoligono = GRUPOS.map((g) => {
    const val = radarDados[g.key as keyof RadarGrupoRiscoContract] ?? 30;
    const { x, y } = getCoordenadas(g.angulo, val);
    return `${x},${y}`;
  }).join(' ');

  const niveis = [20, 40, 60, 80, 100];

  return (
    <div className="flex flex-col items-center">
      <svg
        width={tamanho}
        height={tamanho}
        className="overflow-visible select-none drop-shadow-sm"
        viewBox={`0 0 ${tamanho} ${tamanho}`}
      >
        <defs>
          <radialGradient id="radarFillCustomer" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#3730A3" stopOpacity="0.15" />
          </radialGradient>
        </defs>

        {/* Níveis concêntricos */}
        {niveis.map((nivel) => {
          const pontosNivel = GRUPOS.map((g) => {
            const { x, y } = getCoordenadas(g.angulo, nivel);
            return `${x},${y}`;
          }).join(' ');
          return (
            <polygon
              key={nivel}
              points={pontosNivel}
              fill="none"
              stroke="#E2E8F0"
              strokeWidth="1"
              strokeDasharray={nivel === 100 ? 'none' : '3 3'}
            />
          );
        })}

        {/* Eixos */}
        {GRUPOS.map((g) => {
          const { x, y } = getCoordenadas(g.angulo, 100);
          return (
            <line
              key={g.key}
              x1={center}
              y1={center}
              x2={x}
              y2={y}
              stroke="#CBD5E1"
              strokeWidth="1"
            />
          );
        })}

        {/* Polígono preenchido */}
        <polygon
          points={pontosPoligono}
          fill="url(#radarFillCustomer)"
          stroke="#4F46E5"
          strokeWidth="2.5"
          className="transition-all duration-500"
        />

        {/* Vértices e Rótulos */}
        {GRUPOS.map((g) => {
          const val = radarDados[g.key as keyof RadarGrupoRiscoContract] ?? 30;
          const { x, y } = getCoordenadas(g.angulo, val);
          const posTexto = getCoordenadas(g.angulo, 116);

          return (
            <g key={g.key} className="group">
              <circle
                cx={x}
                cy={y}
                r="4.5"
                fill={g.cor}
                stroke="#FFFFFF"
                strokeWidth="2"
                className="transition-transform group-hover:scale-150 cursor-pointer"
              />
              <title>{`${g.label}: ${val}/100`}</title>

              <text
                x={posTexto.x}
                y={posTexto.y}
                textAnchor="middle"
                dominantBaseline="central"
                className="text-[9.5px] font-bold fill-slate-700 select-none"
              >
                {g.label.split(' ')[0]} ({val}%)
              </text>
            </g>
          );
        })}
      </svg>

      <div className="grid grid-cols-3 gap-x-3 gap-y-1.5 mt-3 text-[10.5px] text-gray-600">
        {GRUPOS.map((g) => (
          <div key={g.key} className="flex items-center gap-1">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: g.cor }}
            />
            <span className="truncate">{g.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
